// API service for connector operations
// Communicates with the backend API instead of doing client-side processing

// Configuration constants
import { CONNECTOR_API_URL, getAuthHeaders } from '../../config/api'

const MOCK_MODE = (import.meta as any).env?.VITE_MOCK_MODE === 'true' || (import.meta as any).env?.DEV
// Every endpoint below is relative to /api/v1/connector ("uploads", "templates", …),
// which is why the service previously 404'd: it built `${host}/uploads`.
const API_BASE = CONNECTOR_API_URL

// Local types for API responses
export interface ConnectorTemplate {
  id: string
  name: string
  description: string
  posSystem: string
  fileType: 'csv' | 'excel'
  delimiter?: string
  hasHeaders: boolean
  fieldMappings: any[]
  createdAt: string
  isSystemTemplate?: boolean
}

export interface ApiError {
  code: string
  message: string
  details?: any
  retryable: boolean
  suggestions?: string[]
}

export class ConnectorApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public retryable: boolean = false,
    public suggestions: string[] = []
  ) {
    super(message)
    this.name = 'ConnectorApiError'
  }
}

export interface UploadResponse {
  success: boolean
  data: {
    uploadId: string
    message: string
  }
  error?: string
}

export interface ProcessResponse {
  success: boolean
  data: {
    message: string
    uploadId: string
  }
  error?: string
}

export interface UploadStatus {
  id: string
  filename: string
  status: 'uploading' | 'processing' | 'completed' | 'failed'
  recordsProcessed: number
  recordsImported: number
  recordsFailed: number
  errors: string[]
  createdAt: string
  updatedAt: string
}

export class ConnectorApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryConfig: { maxRetries?: number; retryDelay?: number } = {}
  ): Promise<T> {
    const { maxRetries = 3, retryDelay = 1000 } = retryConfig

    if (MOCK_MODE) {
      return this.mockRequest<T>(endpoint, options)
    }

    const url = `${API_BASE}${endpoint}`
    let lastError: Error

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // ── Auth headers are REQUIRED ────────────────────────────────────────
        // This service sent NO Authorization header, so every call it made was
        // anonymous and the API answered 401 "Authorization token required".
        // Import History (uploads) and Settings (templates) therefore failed to
        // load while Sync Monitoring and Marketplace worked — those two use the
        // shared `getAuthHeaders()` helper. Reuse the same helper here so all
        // connector calls authenticate identically.
        //
        // `...options` must be spread first and `headers` re-declared after it:
        // spreading options last would let an empty `options.headers` object
        // replace these headers entirely.
        const response = await fetch(url, {
          ...options,
          headers: {
            ...getAuthHeaders(),
            ...options.headers,
          },
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Network error' }))

          // Categorize errors
          let errorCode = 'UNKNOWN_ERROR'
          let retryable = false
          let suggestions: string[] = []

          if (response.status === 400) {
            errorCode = 'VALIDATION_ERROR'
            suggestions = ['Check your input data and try again']
          } else if (response.status === 401) {
            errorCode = 'AUTHENTICATION_ERROR'
            suggestions = ['Please log in again']
          } else if (response.status === 403) {
            errorCode = 'PERMISSION_ERROR'
            suggestions = ['Contact your administrator for access']
          } else if (response.status === 404) {
            errorCode = 'NOT_FOUND'
            suggestions = ['Check if the resource exists']
          } else if (response.status >= 500) {
            errorCode = 'SERVER_ERROR'
            retryable = true
            suggestions = ['Try again in a few moments', 'Contact support if the problem persists']
          } else if (response.status === 429) {
            errorCode = 'RATE_LIMITED'
            retryable = true
            suggestions = ['Wait a moment before trying again']
          }

          const error = new ConnectorApiError(
            errorData.error || `HTTP ${response.status}`,
            errorCode,
            retryable,
            suggestions
          )

          // Retry on retryable errors
          if (retryable && attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, attempt)))
            continue
          }

          throw error
        }

        return response.json()
      } catch (error) {
        lastError = error as Error

        // Retry on network errors
        if (attempt < maxRetries && (error instanceof TypeError || (error as Error).name === 'NetworkError')) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * Math.pow(2, attempt)))
          continue
        }

        break
      }
    }

    throw lastError!
  }

  private async mockRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Mock responses for testing
    await new Promise(resolve => setTimeout(resolve, 500)) // Simulate network delay

    if (endpoint === '/upload' && options.method === 'POST') {
      return {
        success: true,
        data: {
          uploadId: 'mock-upload-' + Date.now(),
          message: 'File uploaded successfully'
        }
      } as T
    }

    if (endpoint.includes('/process') && options.method === 'POST') {
      return {
        success: true,
        data: {
          message: 'Processing started',
          uploadId: endpoint.split('/')[2]
        }
      } as T
    }

    if (endpoint === '/uploads' && options.method === 'GET') {
      return {
        success: true,
        data: [
          {
            id: 'upload-001',
            filename: 'products-square.csv',
            status: 'completed',
            recordsProcessed: 150,
            recordsImported: 145,
            recordsFailed: 5,
            errors: ['Row 12: Invalid price format', 'Row 45: Missing SKU'],
            createdAt: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
            updatedAt: new Date(Date.now() - 86400000 + 300000).toISOString()
          },
          {
            id: 'upload-002',
            filename: 'inventory-update.xlsx',
            status: 'completed',
            recordsProcessed: 89,
            recordsImported: 89,
            recordsFailed: 0,
            errors: [],
            createdAt: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
            updatedAt: new Date(Date.now() - 172800000 + 120000).toISOString()
          },
          {
            id: 'upload-003',
            filename: 'sales-data.csv',
            status: 'failed',
            recordsProcessed: 0,
            recordsImported: 0,
            recordsFailed: 234,
            errors: ['Invalid file format', 'Template not found'],
            createdAt: new Date(Date.now() - 259200000).toISOString(), // 3 days ago
            updatedAt: new Date(Date.now() - 259200000 + 5000).toISOString()
          }
        ]
      } as T
    }

    if (endpoint.includes('/uploads/') && options.method === 'GET') {
      const uploadId = endpoint.split('/')[2]
      return {
        success: true,
        data: {
          id: uploadId,
          filename: 'test-products.csv',
          status: 'completed',
          recordsProcessed: 4,
          recordsImported: 4,
          recordsFailed: 0,
          errors: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      } as T
    }

    if (endpoint === '/templates' && options.method === 'GET') {
      return {
        success: true,
        data: [
          {
            id: 'square-products',
            name: 'Square POS - Products',
            description: 'Import products from Square POS export',
            posSystem: 'square',
            fileType: 'csv',
            hasHeaders: true,
            fieldMappings: [],
            createdAt: new Date().toISOString(),
            isSystemTemplate: true
          },
          {
            id: 'shopify-products',
            name: 'Shopify - Products',
            description: 'Import products from Shopify CSV export',
            posSystem: 'shopify',
            fileType: 'csv',
            hasHeaders: true,
            fieldMappings: [],
            createdAt: new Date(Date.now() - 86400000).toISOString(),
            isSystemTemplate: false
          }
        ]
      } as T
    }

    if (endpoint === '/templates' && options.method === 'POST') {
      const templateData = JSON.parse(options.body as string || '{}')
      return {
        success: true,
        data: {
          id: 'template-' + Date.now(),
          ...templateData,
          createdAt: new Date().toISOString()
        }
      } as T
    }

    if (endpoint.startsWith('/templates/') && options.method === 'PUT') {
      const templateId = endpoint.split('/')[2]
      const updates = JSON.parse(options.body as string || '{}')
      return {
        success: true,
        data: {
          id: templateId,
          name: updates.name || 'Updated Template',
          description: updates.description || 'Updated description',
          posSystem: updates.posSystem || 'square',
          fileType: updates.fileType || 'csv',
          hasHeaders: updates.hasHeaders !== undefined ? updates.hasHeaders : true,
          fieldMappings: updates.fieldMappings || [],
          createdAt: new Date(Date.now() - 86400000).toISOString()
        }
      } as T
    }

    if (endpoint.startsWith('/templates/') && options.method === 'DELETE') {
      return {
        success: true
      } as T
    }

    throw new Error('Mock endpoint not implemented')
  }

  /**
   * Upload a file to the connector API
   */
  async uploadFile(
    file: File,
    options: {
      onProgress?: (progress: number) => void
    } = {}
  ): Promise<UploadResponse> {
    if (MOCK_MODE) {
      // Mock upload with progress
      return new Promise((resolve) => {
        let progress = 0
        const interval = setInterval(() => {
          progress += 25
          if (options.onProgress) {
            options.onProgress(progress)
          }
          if (progress >= 100) {
            clearInterval(interval)
            resolve({
              success: true,
              data: {
                uploadId: 'mock-upload-' + Date.now(),
                message: 'File uploaded successfully'
              }
            })
          }
        }, 200)
      })
    }

    const formData = new FormData()
    formData.append('file', file)
    // Note: templateId is sent in the process request, not upload

    const xhr = new XMLHttpRequest()

    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && options.onProgress) {
          const progress = (event.loaded / event.total) * 100
          options.onProgress(progress)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText)
            resolve(response)
          } catch (error) {
            reject(new Error('Invalid response format'))
          }
        } else {
          try {
            const error = JSON.parse(xhr.responseText)
            reject(new Error(error.error || `HTTP ${xhr.status}`))
          } catch {
            reject(new Error(`HTTP ${xhr.status}`))
          }
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Network error'))
      })

      xhr.open('POST', `${API_BASE}/upload`)
      // Add auth header if needed (temporarily disabled for testing)
      // const token = localStorage.getItem('auth_token')
      // if (token) {
      //   xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      // }
      xhr.send(formData)
    })
  }

  /**
   * Start processing an uploaded file
   */
  async processUpload(uploadId: string, templateId: string): Promise<ProcessResponse> {
    return this.request('/uploads/' + uploadId + '/process', {
      method: 'POST',
      body: JSON.stringify({ templateId }),
    })
  }

  /**
   * Get upload status
   */
  async getUploadStatus(uploadId: string): Promise<UploadStatus> {
    const response = await this.request<{
      success: boolean
      data: UploadStatus
      error?: string
    }>('/uploads/' + uploadId)

    if (!response.success) {
      throw new Error(response.error || 'Failed to get upload status')
    }

    return response.data
  }

  /**
   * Get all uploads for current tenant
   */
  async getUploads(): Promise<UploadStatus[]> {
    const response = await this.request<{
      success: boolean
      data: UploadStatus[]
      error?: string
    }>('/uploads')

    if (!response.success) {
      throw new Error(response.error || 'Failed to get uploads')
    }

    return response.data
  }

  /**
   * Get all templates for current tenant
   */
  async getTemplates(): Promise<ConnectorTemplate[]> {
    const response = await this.request<{
      success: boolean
      data: ConnectorTemplate[]
      error?: string
    }>('/templates')

    if (!response.success) {
      throw new Error(response.error || 'Failed to get templates')
    }

    return response.data
  }

  /**
   * Create a new template
   */
  async createTemplate(template: Omit<ConnectorTemplate, 'id' | 'createdAt'>): Promise<{ success: boolean; data: ConnectorTemplate; error?: string }> {
    return this.request('/templates', {
      method: 'POST',
      body: JSON.stringify(template)
    })
  }

  /**
   * Update an existing template
   */
  async updateTemplate(templateId: string, updates: Partial<ConnectorTemplate>): Promise<{ success: boolean; data: ConnectorTemplate; error?: string }> {
    return this.request(`/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    })
  }

  /**
   * Delete a template
   */
  async deleteTemplate(templateId: string): Promise<{ success: boolean; error?: string }> {
    return this.request(`/templates/${templateId}`, {
      method: 'DELETE'
    })
  }

  /**
   * Update field mappings for an upload
   */
  async updateMappings(
    uploadId: string,
    mappings: Record<string, string>
  ): Promise<{ success: boolean; message: string }> {
    return this.request('/uploads/' + uploadId + '/mapping', {
      method: 'POST',
      body: JSON.stringify({ mappings }),
    })
  }

  /**
   * Poll upload status until completion
   */
  async pollUploadStatus(
    uploadId: string,
    onProgress?: (status: UploadStatus) => void,
    pollInterval = 2000
  ): Promise<UploadStatus> {
    if (MOCK_MODE) {
      return new Promise((resolve) => {
        let attempts = 0
        const maxAttempts = 5

        const poll = () => {
          attempts++
          const mockStatus: UploadStatus = {
            id: uploadId,
            filename: 'test-products.csv',
            status: attempts >= maxAttempts ? 'completed' : 'processing',
            recordsProcessed: attempts * 1,
            recordsImported: attempts >= maxAttempts ? 4 : attempts * 1,
            recordsFailed: 0,
            errors: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }

          if (onProgress) {
            onProgress(mockStatus)
          }

          if (mockStatus.status === 'completed' || mockStatus.status === 'failed') {
            resolve(mockStatus)
          } else {
            setTimeout(poll, pollInterval)
          }
        }

        poll()
      })
    }

    return new Promise((resolve) => {
      const poll = async () => {
        try {
          const status = await this.getUploadStatus(uploadId)

          if (onProgress) {
            onProgress(status)
          }

          if (status.status === 'completed' || status.status === 'failed') {
            resolve(status)
          } else {
            setTimeout(poll, pollInterval)
          }
        } catch (error) {
          // Retry on error
          setTimeout(poll, pollInterval)
        }
      }

      poll()
    })
  }
}

// Export singleton instance
export const connectorApi = new ConnectorApiService()